import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner } from '../shared/components';
import { emptyState, callout } from '../shared/components/Narrative';
import { infoIcon } from '../shared/components/InfoIcon';
import { icon } from '../shared/components/icons';
import { api } from '../shared/services/api';
import { paginate, filterBar, pager } from '../shared/lib/tableControls';
import { PublishStatements } from './ownerOps/PublishStatements';   // R117: statements → owner accounts + portals

// ── Owners & Properties ─────────────────────────────────────────────────────
// The surge equivalent of wchdash's "Properties & Owners" admin. Two sections:
//   1. OWNERS — the owner-client roster (CRUD), backed by app_settings 'owners'.
//   2. PROPERTIES — every active listing, with its owner / property-type / comp
//      tier assignable inline (saves in place, no save button — like wchdash).
// This is the foundation for owner-facing monthly reports: reports read which
// properties belong to which owner from these assignments.
//
// Data model (see REDESIGN_CONVENTIONS / server.js getCompPercentiles pattern):
//   app_settings 'owners'            = [{ id, name, email, phone, notes, active, created_at }]
//   app_settings 'owner_assignments' = { "<pms_listing_id>": "<owner_id>" }
// A listing's identity is its Hostaway pms_listing_id (GET /api/listings aliases
// it as `id`).

interface OwnerMember { first_name: string; last_name: string; email?: string; phone?: string; role?: string; }
interface Owner {
  id: string;
  name: string;
  first_name?: string;        // R100 C1
  last_name?: string;
  members?: OwnerMember[];    // R100 C2: co-owners / spouse on the same statement
  mailing_address?: { line1?: string; line2?: string; city?: string; state?: string; zip?: string; country?: string } | null;   // R100 C3
  email?: string;
  phone?: string;
  notes?: string;
  contracts_url?: string; // legacy R48 Drive link — superseded by uploaded contract / W-9 / Form 590 (R100 C4)
  active?: boolean;
  created_at?: string;
  docs_summary?: Record<string, number>;   // from owner_profiles (server-joined)
  form_at?: string | null;                 // R100 B11: owner form submitted
}

const PROPERTY_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'standard',       label: 'Standard' },
  { value: 'mtr',            label: 'MTR' },
  { value: 'inactive',       label: 'Inactive' },
  { value: 'owner_occupied', label: 'Owner-occupied' },
];

const COMP_TIER_OPTIONS: { value: number; label: string }[] = [
  { value: 25, label: 'p25' },
  { value: 50, label: 'p50' },
  { value: 75, label: 'p75' },
  { value: 90, label: 'p90' },
];

// Cell-level inline style used only for ad-hoc per-cell overrides on top of the
// standard .data-table look (alignment, weight, colour). The base padding /
// border / font come from .data-table itself.
const TD = 'vertical-align:middle;';

export class OwnersPage {
  private container: HTMLElement;
  private owners: Owner[] = [];
  private assignments: Record<string, string> = {}; // pms_listing_id -> owner_id
  private propertyTypes: Record<string, string> = {}; // pms_listing_id -> type
  private compPercentiles: Record<string, number> = {}; // pms_listing_id -> 25/50/75/90
  private listings: any[] = [];
  private editingOwnerId: string | null = null; // owner row currently in edit mode
  private importPlan: any = null;               // R100 C5: dry-run plan awaiting Apply
  private importCsv = '';                        // the CSV text behind the plan
  private importBusy = false;
  private adding = false; // inline "add owner" row open
  private propSearch = '';            // properties-table search (name/id)
  private propPage = 1;               // properties-table current page (1-based)
  private readonly propPageSize = 25; // properties per page

  constructor(container: HTMLElement) {
    this.container = container;
  }

  async render(): Promise<void> {
    this.container.innerHTML = `
      <div class="page-content">
        <div class="dash-head">
          <h1>Owners &amp; Properties</h1>
          <p>Manage owner clients and which properties belong to each — the foundation for owner-facing monthly reports.</p>
        </div>
        <div id="owners-publish-host"></div>
        <div id="owners-page-body">${LoadingSpinner.render()}</div>
      </div>`;

    await this.loadData();
    this.renderBody();
  }

  // ── Data ─────────────────────────────────────────────────────

  private async loadData(): Promise<void> {
    // R82 #4b: owner notes recorded on property sheets, joined per owner here.
    // R87 #3: the projects/escalations board + NPS pulse ride the same
    // async-card pattern — they must never block the owners table.
    fetch('/api/owners/projects-board').then(r => r.ok ? r.json() : null).then(j => {
      if (j) { this.projectsBoard = j; this.drawProjects(); }
    }).catch(() => { /* card stays absent */ });
    fetch('/api/owners/nps-summary').then(r => r.ok ? r.json() : null).then(j => {
      if (j) { this.npsSummary = j; this.drawNps(); }
    }).catch(() => { /* card stays absent */ });
    fetch('/api/ob/owner-notes').then(r => r.ok ? r.json() : null).then(j => {
      if (j && Array.isArray(j.rows)) { this.ownerNotes = j.rows; this.drawOwnerNotes(); }
    }).catch(() => { /* the card just stays absent */ });
    const [owners, assignments, listings, propTypes, compPcts] = await Promise.all([
      api.get<{ owners: Owner[] }>('/owners'),
      api.get<{ assignments: Record<string, string> }>('/owners/assignments'),
      api.get<any[]>('/listings'),
      api.get<{ types: Record<string, string> }>('/listings/property-types'),
      api.get<{ percentiles: Record<string, number> }>('/listings/comp-percentiles'),
    ]);
    this.owners = owners?.owners || [];
    this.assignments = assignments?.assignments || {};
    this.listings = (listings || []).filter(l => (l.status || 'active') === 'active');
    this.propertyTypes = propTypes?.types || {};
    this.compPercentiles = compPcts?.percentiles || {};
  }

  private ownerName(id: string | undefined): string {
    if (!id) return '';
    const o = this.owners.find(o => String(o.id) === String(id));
    return o ? o.name : '';
  }

  private propertyCountFor(ownerId: string): number {
    return Object.values(this.assignments).filter(v => String(v) === String(ownerId)).length;
  }

  // ── Body render ──────────────────────────────────────────────

  private renderBody(): void {
    const body = document.getElementById('owners-page-body');
    if (!body) return;
    body.innerHTML = `
      ${this.renderKpis()}
      <div id="owner-projects-card"></div>
      <div id="owner-nps-card"></div>
      ${this.renderOwnersSection()}
      <div id="owner-notes-card"></div>
      ${this.renderPropertiesSection()}
      ${this.renderChurnSection()}
    `;
    this.bindEvents();
    this.loadChurn();  this.drawOwnerNotes();  this.drawProjects();  this.drawNps();   // re-paint the async cards after every body rebuild
  }

  // ── Summary KPIs ─────────────────────────────────────────────
  private kpiCard(label: string, icoName: string, value: string, cap: string, tone = ''): string {
    return `
      <div class="kpi-card ${tone}">
        <div class="kpi-card-top"><span class="kpi-card-label">${label}</span><span class="kpi-card-ic">${icon(icoName, 17)}</span></div>
        <div class="kpi-card-value">${value}</div>
        <div class="kpi-card-cap">${cap}</div>
      </div>`;
  }

  private renderKpis(): string {
    const totalOwners = this.owners.length;
    const activeOwners = this.owners.filter(o => o.active !== false).length;
    const activeProps = this.listings.length;
    const assignedProps = this.listings.filter(l => this.assignments[String(l.id)]).length;
    const unassigned = activeProps - assignedProps;
    return `
      <div class="kpi-grid">
        ${this.kpiCard('Owners', 'users', String(totalOwners), `${activeOwners} active`)}
        ${this.kpiCard('Active owners', 'heart', String(activeOwners), totalOwners ? `of ${totalOwners} total` : 'no owners yet', activeOwners ? 'tone-good' : '')}
        ${this.kpiCard('Active properties', 'building', String(activeProps), 'synced from Hostaway')}
        ${this.kpiCard('Assigned', 'flag', String(assignedProps), `${unassigned} unassigned`, unassigned > 0 ? 'tone-bad' : 'tone-good')}
      </div>`;
  }

  // ── Owners section ───────────────────────────────────────────

  // ── Churn log (Notes-4 i: who left, when, and why) ───────────
  private renderChurnSection(): string {
    return `
      <div class="pf-section" style="margin-top:20px;">
        <div class="pf-section-hdr">
          <div>
            <h3>Departures</h3>
            <p>Owners and properties that left us, with the why — feeds the churn KPI on Today</p>
          </div>
          <button class="btn btn-secondary btn-sm" id="churn-add-property" style="margin-left:auto;">Log a property departure…</button>
        </div>
        <div id="churn-log" style="padding:6px 18px 14px;">
          <div style="font-size:12px; color:var(--text-muted); padding:8px 0;">Loading…</div>
        </div>
      </div>`;
  }

  private async loadChurn(): Promise<void> {
    const el = document.getElementById('churn-log');
    if (!el) return;
    try {
      const r = await fetch('/api/churn-events');
      const { events } = r.ok ? await r.json() : { events: [] };
      if (!events.length) {
        el.innerHTML = '<div style="font-size:12px; color:var(--text-faint, #9ca3af); padding:8px 0;">No departures logged. Use "Departed…" on an owner row, or "Log a property departure".</div>';
        return;
      }
      const REASON_PILL: Record<string, string> = { we_fired: 'pill-blue', we_screwed_up: 'pill-red', other: 'pill-gray' };
      el.innerHTML = events.sort((a: any, b: any) => (b.departed_at || '').localeCompare(a.departed_at || '')).map((e: any) => `
        <div style="display:flex; align-items:center; gap:10px; padding:7px 0; border-bottom:1px solid var(--border-soft); font-size:12px;">
          <span class="mono" style="color:var(--text-muted); width:80px;">${e.departed_at}</span>
          <span class="pill pill-gray">${e.kind}</span>
          <span style="font-weight:600; min-width:0;">${Formatters.escapeHtml(e.name)}</span>
          <span class="pill ${REASON_PILL[e.reason] || REASON_PILL.other}">${(e.reason || '').replace(/_/g, ' ')}</span>
          ${e.notes ? `<span style="color:var(--text-muted); min-width:0;" class="u-truncate">${Formatters.escapeHtml(e.notes)}</span>` : ''}
          <span style="flex:1;"></span>
          <button class="btn btn-secondary btn-sm churn-del-btn" data-id="${e.id}" style="font-size:10px; padding:2px 8px;">remove</button>
        </div>`).join('');
      el.querySelectorAll<HTMLElement>('.churn-del-btn').forEach(b => b.addEventListener('click', async () => {
        await fetch('/api/churn-events/delete', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: b.dataset.id }) });
        this.loadChurn();
      }));
    } catch (_) { el.innerHTML = '<div style="font-size:12px; color:var(--danger);">Failed to load departures.</div>'; }
  }

  // Three prompts beat a modal we don't have: reason, date, optional note.
  private async markDeparted(kind: 'owner' | 'property', id: string | null, name: string): Promise<void> {
    const reasonRaw = window.prompt(`Why did ${name} leave?\n1 = we fired them\n2 = we screwed up\n3 = other`, '3');
    if (reasonRaw == null) return;
    const reason = ({ '1': 'we_fired', '2': 'we_screwed_up', '3': 'other' } as Record<string, string>)[reasonRaw.trim()] || 'other';
    const departed = window.prompt('Departure date (YYYY-MM-DD):', new Date().toISOString().split('T')[0]);
    if (departed == null) return;
    const notes = window.prompt('Notes (optional):', '') || '';
    await fetch('/api/churn-events', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({
      kind, owner_id: kind === 'owner' ? id : null, listing_id: kind === 'property' ? id : null,
      name, reason, notes, departed_at: departed.trim(),
    }) });
    this.loadChurn();
  }

  private ownerNotes: any[] = [];   // /api/ob/owner-notes rows
  private projectsBoard: any = null;   // R87 #3.1
  private npsSummary: any = null;      // R87 #3.2

  private drawProjects(): void {
    const host = document.getElementById('owner-projects-card');
    const j = this.projectsBoard;
    if (!host || !j || !Array.isArray(j.rows) || !j.rows.length) { if (host) host.innerHTML = ''; return; }
    const esc2 = (x: any) => Formatters.escapeHtml(String(x == null ? '' : x));
    host.innerHTML = `
      <div class="card card-elev" style="margin-bottom:16px; padding:0; overflow:hidden;">
        <div class="card-head" style="padding:11px 16px; display:flex; gap:10px; align-items:center;">
          <span class="card-title">🧗 Owner projects & escalations</span>
          <span style="font-size:11.5px; color:var(--text-muted);">${j.totals.open} open across ${j.totals.owners} owner${j.totals.owners === 1 ? '' : 's'}${j.totals.escalated ? ` · <strong style="color:var(--danger-text,#991b1b);">${j.totals.escalated} escalated</strong>` : ''}</span>
        </div>
        <div style="padding:8px 16px 14px; display:grid; grid-template-columns:repeat(auto-fill, minmax(300px, 1fr)); gap:10px;">
          ${j.rows.map((r: any) => `
            <div style="border:1px solid var(--border); border-radius:10px; padding:9px 12px; ${r.escalated_count ? 'border-color:var(--danger-border,#fecaca); background:var(--danger-soft,#fef2f2);' : ''}">
              <div style="display:flex; gap:8px; align-items:center; margin-bottom:5px;">
                <a href="javascript:void 0" class="owner-profile-link" data-id="${esc2(r.owner.id)}" style="font-size:12.5px; font-weight:700; color:inherit;">${esc2(r.owner.name)}</a>
                <span class="pill pill-gray num" style="margin-left:auto;">${r.open_count} open</span>
              </div>
              ${r.tasks.slice(0, 5).map((t: any) => `
                <div style="display:flex; gap:6px; align-items:flex-start; font-size:12px; padding:2px 0;">
                  <button data-opj-esc="${esc2(r.owner.id)}|${esc2(t.id)}" title="${t.escalated ? 'Escalated — click to de-escalate' : 'Escalate — floats to the top for everyone'}" style="flex:none; border:none; background:none; cursor:pointer; font-size:12px; ${t.escalated ? '' : 'opacity:.35; filter:grayscale(1);'}">⚠️</button>
                  <span style="flex:1;">${esc2(t.text)} <span style="font-size:10px; color:var(--text-faint,#94a3b8);">${t.who === 'owner' ? 'owner\'s' : 'ours'} · ${esc2(t.by || '')}</span></span>
                  <input type="checkbox" data-opj-done="${esc2(r.owner.id)}|${esc2(t.id)}" title="Mark done" style="flex:none; margin-top:2px;">
                </div>`).join('')}
              ${r.tasks.length > 5 ? `<div style="font-size:10.5px; color:var(--text-muted);">+${r.tasks.length - 5} more on the profile</div>` : ''}
            </div>`).join('')}
        </div>
      </div>`;
    const refetch = () => fetch('/api/owners/projects-board').then(r => r.ok ? r.json() : null).then(j2 => { if (j2) { this.projectsBoard = j2; this.drawProjects(); } }).catch(() => { /* keep current */ });
    const act = async (key: string, op: string): Promise<boolean> => {
      const sep = key.indexOf('|');
      const oid = key.slice(0, sep); const tid = key.slice(sep + 1);
      try {
        const r = await fetch(`/api/owners/${encodeURIComponent(oid)}/profile/task`, {
          method: 'POST', headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ op, task_id: tid, by: this.byWho() }),
        });
        if (!r.ok) { const j3 = await r.json().catch(() => ({} as any)); throw new Error(j3.error || `HTTP ${r.status}`); }
        await refetch();
        return true;
      } catch (e: any) { alert(`Didn't save — ${e.message}`); return false; }
    };
    host.querySelectorAll<HTMLElement>('[data-opj-esc]').forEach(b => b.addEventListener('click', () => void act(b.dataset.opjEsc!, 'escalate')));
    host.querySelectorAll<HTMLInputElement>('[data-opj-done]').forEach(b => b.addEventListener('change', async () => {
      // review #41: a failed save left the checkbox in its new state — visually
      // "done" while the store still says open. Snap it back on failure.
      if (!(await act(b.dataset.opjDone!, 'toggle'))) b.checked = !b.checked;
    }));
    host.querySelectorAll<HTMLElement>('.owner-profile-link').forEach(a => a.addEventListener('click', () => {
      sessionStorage.setItem('owner_profile_id', (a as HTMLElement).dataset.id!);
      document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'owner-profile' }));
    }));
  }

  private byWho(): string {
    try { const p2 = JSON.parse(sessionStorage.getItem('userProfile') || '{}'); return String(p2.full_name || p2.email || 'office'); } catch { return 'office'; }
  }

  private drawNps(): void {
    const host = document.getElementById('owner-nps-card');
    const j = this.npsSummary;
    if (!host || !j) return;
    const esc2 = (x: any) => Formatters.escapeHtml(String(x == null ? '' : x));
    host.innerHTML = `
      <div class="card card-elev" style="margin-bottom:16px; padding:14px 16px;">
        <div style="display:flex; gap:14px; align-items:center; flex-wrap:wrap;">
          <span class="card-title">📮 Owner NPS</span>
          ${j.nps == null
            ? '<span style="font-size:12.5px; color:var(--text-muted);">No responses yet — copy each owner\'s survey link from their profile and send it out.</span>'
            : `<span class="num" style="font-size:26px; font-weight:800; ${j.nps >= 30 ? 'color:var(--success-text,#065f46);' : j.nps < 0 ? 'color:var(--danger-text,#991b1b);' : ''}">${j.nps > 0 ? '+' : ''}${j.nps}</span>
               <span style="font-size:11.5px; color:var(--text-muted);">${j.responses} response${j.responses === 1 ? '' : 's'} · ${j.promoters} promoters · ${j.passives} passives · ${j.detractors} detractors · trailing 365d</span>`}
        </div>
        ${(j.latest || []).length ? `<div style="margin-top:8px; display:flex; flex-direction:column; gap:4px;">
          ${j.latest.slice(0, 4).filter((r2: any) => r2.comment).map((r2: any) => `<div style="font-size:12px; color:var(--text-muted);">“${esc2(r2.comment)}” <span style="font-size:10.5px;">— ${r2.anonymous ? 'anonymous' : esc2(r2.owner_name || 'owner')} · ${esc2(String(r2.score))}/10</span></div>`).join('')}
        </div>` : ''}
      </div>`;
  }
  // R82 #4b (Jesse: "an owner notes section on the PDS that filters into the
  // owner profile notes"): per-owner aggregation of the PDS Owner Notes
  // sections across their properties. Rendered into its own host div so the
  // async fetch never fights the main draw.
  private drawOwnerNotes(): void {
    const host = document.getElementById('owner-notes-card');
    if (!host || !this.ownerNotes.length) return;
    const esc = (x: any) => String(x == null ? '' : x).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const byOwner = new Map<string, any[]>();
    for (const row of this.ownerNotes) {
      const oid = String((this.assignments || {})[row.listing_id] || '');
      const key = oid || '(unassigned property)';
      if (!byOwner.has(key)) byOwner.set(key, []);
      byOwner.get(key)!.push(row);
    }
    const nameOfOwner = (oid: string) => {
      const o = this.owners.find((x: any) => String(x.id) === oid);
      return o ? (o as any).name : oid;
    };
    host.innerHTML = `
      <div class="card" style="margin-top:16px; padding:14px 18px;">
        <div style="font-size:13px; font-weight:700; margin-bottom:2px;">📝 Owner notes from property sheets</div>
        <div style="font-size:11.5px; color:var(--text-muted); margin-bottom:10px;">Recorded in each property's PDS → Owner Notes section (how the owner likes to work — call cadence, preferences). Edit them on the property sheet; they aggregate here per owner. Never printed on cleaner or guest documents.</div>
        ${[...byOwner.entries()].map(([oid, rows]) => `
          <div style="margin-bottom:10px;">
            <div style="font-size:12.5px; font-weight:700;">${esc(oid === '(unassigned property)' ? oid : nameOfOwner(oid))}</div>
            ${rows.map((r: any) => `
              <div style="font-size:12px; padding:2px 0 2px 12px;">
                <span style="color:var(--text-muted);">${esc(r.name)}:</span>
                ${r.notes.map((n: any) => `<span style="margin-left:4px;"><strong>${esc(n.field)}</strong>${n.value ? ` — ${esc(n.value)}` : ''}${n.note ? ` <em style="color:var(--text-muted);">📝 ${esc(n.note)}</em>` : ''}</span>`).join(' · ')}
              </div>`).join('')}
          </div>`).join('')}
      </div>`;
  }

  private renderOwnersSection(): string {
    const rows = this.owners.length || this.adding
      ? `<div class="card" style="border-radius:0 0 var(--radius-lg) var(--radius-lg); border-top:none;">
          <div class="u-table-wrap">
            <table class="data-table" style="min-width:920px;">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Notes</th>
                  <th title="Contract · W-9 · Form 590 uploaded on the profile, and whether the owner form came back">Paperwork</th>
                  <th class="num" style="text-align:center;"># Properties</th>
                  <th style="text-align:center;">Active</th>
                  <th style="text-align:right;">Actions</th>
                </tr>
              </thead>
              <tbody id="owners-table-body">
                ${this.owners.map(o => this.renderOwnerRow(o)).join('')}
                ${this.adding ? this.renderOwnerEditRow(null) : ''}
              </tbody>
            </table>
          </div>
        </div>`
      : `<div class="card" style="border-radius:0 0 var(--radius-lg) var(--radius-lg); border-top:none;">
          ${emptyState('No owners yet', 'Add an owner client to start assigning properties. Owner reports are built per owner from the assignments below.', '👤')}
        </div>`;

    return `
      <div style="margin-bottom:var(--space-6);">
        <div class="card-head" style="border:1px solid var(--border); border-radius:var(--radius-lg) var(--radius-lg) 0 0;">
          <div style="display:flex; align-items:center; gap:var(--space-2); flex-wrap:wrap; min-width:0;">
            <span class="card-title" style="font-size:15px;">Owners</span>
            <span style="font-size:var(--fs-body); color:var(--text-muted);">${this.owners.length} owner${this.owners.length !== 1 ? 's' : ''}</span>
          </div>
          <button class="btn btn-secondary btn-sm" id="owners-publish-btn" style="margin-left:auto;" title="Upload a month of VR Platform statement PDFs (or pick an uploaded batch) — creates the owner accounts, assigns their properties and puts each statement on its owner's portal. Plan first; nothing is written until you publish.">📊 Publish statements</button>
          <label class="btn btn-ghost btn-sm" style="cursor:pointer;" title="Bulk-create owners + their property assignments from the VR Platform owner-statements CSV (Listing, Owners, …). Dry run first — nothing is written until you apply.">⬆ CSV import<input type="file" id="owners-import-file" accept=".csv,text/csv" style="display:none;"></label>
          <button class="btn btn-primary btn-sm" id="add-owner-btn" ${this.adding ? 'disabled' : ''}>+ Add owner</button>
        </div>
        ${this.renderImportPreview()}
        ${rows}
      </div>`;
  }

  // The shared api helper swallows the server's error text — the import needs it.
  private async postImport(payload: any): Promise<any> {
    try {
      const r = await fetch('/api/owners/import', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ ...payload, by: this.byWho() }) });
      const j = await r.json().catch(() => ({} as any));
      return r.ok ? j : { error: j.error || `HTTP ${r.status}` };
    } catch (e: any) { return { error: e.message || 'network' }; }
  }

  // R100 C5: the dry-run plan — what will be created / assigned / left alone.
  private renderImportPreview(): string {
    const p = this.importPlan;
    if (!p) return '';
    const E = (x: any) => Formatters.escapeHtml(String(x == null ? '' : x));
    if (p.error) return `<div class="card" style="border-top:none; border-radius:0; padding:12px 16px; color:var(--danger-text,#991b1b); font-size:12.5px;">Import preview failed — ${E(p.error)} <button class="btn btn-secondary btn-sm" id="owners-import-cancel" style="margin-left:8px;">Close</button></div>`;
    const st = p.stats || {};
    const ACT: Record<string, string> = { assign: 'pill-green', keep: 'pill-gray', conflict: 'pill-amber', unmatched: 'pill-red', duplicate_in_csv: 'pill-red' };
    const ACT_LABEL: Record<string, string> = { assign: 'assign', keep: 'already theirs', conflict: 'assigned to someone else — left alone', unmatched: 'no matching listing', duplicate_in_csv: 'listed under two owners in the CSV' };
    const groups: any[] = p.groups || [];
    return `
      <div class="card" style="border-top:none; border-radius:0; padding:12px 16px; background:var(--accent-soft,#eaf2ff);">
        <div style="display:flex; gap:10px; align-items:center; flex-wrap:wrap; margin-bottom:8px;">
          <span style="font-size:13px; font-weight:700;">${p.dry_run ? 'Import preview' : 'Import applied'} — ${E(p.rows)} CSV rows → ${E(st.groups)} owner${st.groups === 1 ? '' : 's'}</span>
          <span class="pill pill-green">${E(st.create)} new</span><span class="pill pill-gray">${E(st.match)} already exist</span>
          <span class="pill pill-green">${E(st.assign)} assignments</span>${st.keep ? `<span class="pill pill-gray">${E(st.keep)} unchanged</span>` : ''}${st.conflict ? `<span class="pill pill-amber" title="The listing already points at a different owner — not changed">${E(st.conflict)} conflicts</span>` : ''}${st.unmatched ? `<span class="pill pill-red" title="No unique listing matched the CSV name — assign by hand below">${E(st.unmatched)} unmatched</span>` : ''}${st.duplicate_in_csv ? `<span class="pill pill-red">${E(st.duplicate_in_csv)} duplicated in CSV</span>` : ''}
          ${p.applied ? `<span style="font-size:12px; color:var(--text-muted);">created ${E(p.applied.created)} · assigned ${E(p.applied.assigned)}${p.applied.overwritten ? ` · overwrote ${E(p.applied.overwritten)}` : ''} · backup in app_settings ${E(p.applied.backup)}</span>` : ''}
          <span style="margin-left:auto; display:flex; gap:6px;">
            ${p.dry_run ? `<button class="btn btn-primary btn-sm" id="owners-import-apply" ${this.importBusy ? 'disabled' : ''}>${this.importBusy ? 'Applying…' : `Apply — create ${E(st.create)} owner${st.create === 1 ? '' : 's'}, set ${E(st.assign)} assignment${st.assign === 1 ? '' : 's'}`}</button>` : ''}
            <button class="btn btn-secondary btn-sm" id="owners-import-cancel">${p.dry_run ? 'Cancel' : 'Close'}</button>
          </span>
        </div>
        <div style="font-size:11.5px; color:var(--text-muted); margin-bottom:8px;">Co-owners on one statement become ONE owner record (first person is the primary, the rest are co-owners). Only EMPTY assignments are filled; a listing that already belongs to another owner is reported, never moved. The current owners + assignments are backed up before anything is written.</div>
        <div style="max-height:360px; overflow:auto; border:1px solid var(--border); border-radius:8px; background:var(--card,#fff);">
          ${groups.map(g => `
            <div style="display:flex; gap:10px; align-items:flex-start; padding:7px 10px; border-bottom:1px solid var(--border-soft,#f1f5f9); font-size:12px;">
              <span class="pill ${g.action === 'create' ? 'pill-green' : 'pill-gray'}" style="flex:none; min-width:52px; text-align:center;">${g.action === 'create' ? 'new' : 'exists'}</span>
              <div style="min-width:220px;"><strong>${E(g.name)}</strong>${g.existing_name && g.existing_name !== g.name ? `<div style="color:var(--text-muted);">→ ${E(g.existing_name)}</div>` : ''}${(g.members || []).length ? `<div style="color:var(--text-muted);">co-owner${g.members.length === 1 ? '' : 's'}: ${g.members.map((m: any) => E([m.first_name, m.last_name].filter(Boolean).join(' '))).join(', ')}</div>` : ''}</div>
              <div style="flex:1; display:flex; flex-direction:column; gap:2px;">${(g.listings || []).map((l: any) => `<div><span class="pill ${ACT[l.action] || 'pill-gray'}" style="font-size:10px;">${E(ACT_LABEL[l.action] || l.action)}</span> ${E(l.csv_name)}${l.listing_name && l.listing_name !== l.csv_name ? ` <span style="color:var(--text-muted);">→ ${E(l.listing_name)}</span>` : ''}${l.action === 'unmatched' && (l.candidates || []).length ? ` <span style="color:var(--text-muted);">(ambiguous: ${l.candidates.map(E).join(' / ')})</span>` : ''}${l.action === 'conflict' ? ` <span style="color:var(--text-muted);">(currently ${E(this.ownerName(l.current_owner_id) || l.current_owner_id)})</span>` : ''}</div>`).join('')}</div>
            </div>`).join('')}
        </div>
      </div>`;
  }

  private renderOwnerRow(o: Owner): string {
    if (this.editingOwnerId === String(o.id)) return this.renderOwnerEditRow(o);
    const active = o.active !== false;
    return `
      <tr data-owner-id="${Formatters.escapeHtml(String(o.id))}">
        <td style="${TD} font-weight:600;"><a href="javascript:void 0" class="owner-profile-link" data-id="${Formatters.escapeHtml(String(o.id))}" title="Open ${Formatters.escapeHtml(o.name)}'s profile — photo, documents, tasks, notes, performance" style="color:inherit; text-decoration:none; border-bottom:1px dashed var(--border);">${Formatters.escapeHtml(o.name)}</a>${(o.members || []).length ? `<div style="font-size:10.5px; color:var(--text-muted); font-weight:400;">👥 ${o.members!.map(m => Formatters.escapeHtml([m.first_name, m.last_name].filter(Boolean).join(' '))).join(', ')}</div>` : ''}${o.mailing_address && (o.mailing_address.city || o.mailing_address.line1) ? `<div style="font-size:10.5px; color:var(--text-muted); font-weight:400;">📬 ${Formatters.escapeHtml([o.mailing_address.city, o.mailing_address.state].filter(Boolean).join(', ') || o.mailing_address.line1 || '')}</div>` : ''}</td>
        <td style="${TD} color:var(--text-muted);">${Formatters.escapeHtml(o.email || '—')}</td>
        <td style="${TD} color:var(--text-muted);">${Formatters.escapeHtml(o.phone || '—')}</td>
        <td class="u-truncate" style="${TD} color:var(--text-muted); max-width:260px;" title="${Formatters.escapeHtml(o.notes || '')}">${Formatters.escapeHtml(o.notes || '—')}</td>
        <td style="${TD} white-space:nowrap;">${(() => { const d = o.docs_summary || {}; const chip = (k: string, l: string) => `<span class="pill ${d[k] ? 'pill-green' : 'pill-amber'}" style="font-size:10px; margin-right:3px;" title="${d[k] ? `${l} on file (${d[k]})` : `${l} not uploaded — open the profile → Documents`}">${d[k] ? '✓' : '○'} ${l}</span>`; return chip('contract', 'Contract') + chip('w9', 'W-9') + chip('form590', '590') + `<span class="pill ${o.form_at ? 'pill-green' : 'pill-gray'}" style="font-size:10px;" title="${o.form_at ? `owner form submitted ${String(o.form_at).slice(0, 10)}` : 'owner form not submitted — send the link from the profile'}">${o.form_at ? '✓' : '○'} form</span>` + (o.contracts_url ? ` <a href="${Formatters.escapeHtml(o.contracts_url)}" target="_blank" rel="noopener" style="font-size:10.5px;" title="Legacy Drive link">📁</a>` : ''); })()}</td>
        <td class="num" style="${TD} text-align:center; font-weight:600;">${this.propertyCountFor(String(o.id))}</td>
        <td style="${TD} text-align:center;">
          <span class="pill ${active ? 'pill-green' : 'pill-gray'}">${active ? 'Active' : 'Inactive'}</span>
        </td>
        <td style="${TD} text-align:right; white-space:nowrap;">
          <button class="btn btn-secondary btn-sm owner-profile-link" data-id="${Formatters.escapeHtml(String(o.id))}" title="The owner's file — photo, documents, tasks, notes, performance">profile</button>
          <button class="btn btn-secondary btn-sm owner-report-btn" data-id="${Formatters.escapeHtml(String(o.id))}" title="Open this owner's monthly report deck" style="margin-left:4px;">report →</button>
          <button class="btn btn-secondary btn-sm edit-owner-btn" data-id="${Formatters.escapeHtml(String(o.id))}" style="margin-left:4px;">Edit</button>
          <button class="btn btn-secondary btn-sm depart-owner-btn" data-id="${Formatters.escapeHtml(String(o.id))}" data-name="${Formatters.escapeHtml(o.name)}" style="margin-left:4px;">Departed…</button>
          <button class="btn btn-sm btn-danger delete-owner-btn" data-id="${Formatters.escapeHtml(String(o.id))}" data-name="${Formatters.escapeHtml(o.name)}" style="margin-left:4px;">Delete</button>
        </td>
      </tr>`;
  }

  // Inline editable row — used both for "add" (owner=null) and "edit".
  // R100 C1–C4: first + last name, co-owners, a real mailing address; the
  // Drive link is gone (contract / W-9 / Form 590 upload on the profile).
  private renderOwnerEditRow(o: Owner | null): string {
    const idAttr = o ? Formatters.escapeHtml(String(o.id)) : '';
    const E = (x: any) => Formatters.escapeHtml(String(x == null ? '' : x));
    const checked = !o || o.active !== false ? 'checked' : '';
    const inp = 'padding:6px 9px; border:1px solid var(--border); border-radius:6px; font-size:12.5px; background:var(--card,#fff); width:100%; box-sizing:border-box;';
    const members: OwnerMember[] = [...((o && o.members) || [])];
    while (members.length < 2) members.push({ first_name: '', last_name: '' });
    const a = (o && o.mailing_address) || {};
    return `
      <tr class="owner-edit-row" data-owner-id="${idAttr}" style="background:var(--accent-soft);">
        <td colspan="8" style="padding:12px 14px;">
          <div style="display:grid; grid-template-columns:repeat(auto-fit, minmax(160px, 1fr)); gap:8px;">
            <label style="font-size:11px; color:var(--text-muted);">First name<input type="text" class="owner-f-first" value="${o ? E(o.first_name || '') : ''}" placeholder="Maria" style="${inp}"></label>
            <label style="font-size:11px; color:var(--text-muted);">Last name<input type="text" class="owner-f-last" value="${o ? E(o.last_name || '') : ''}" placeholder="Nelson" style="${inp}"></label>
            <label style="font-size:11px; color:var(--text-muted);">Display name <span title="How the owner appears everywhere — defaults to First Last (add '& …' for couples)">ⓘ</span><input type="text" class="owner-f-name" value="${o ? E(o.name) : ''}" placeholder="Maria Nelson & Geoff Waxler" style="${inp}"></label>
            <label style="font-size:11px; color:var(--text-muted);">Email<input type="email" class="owner-f-email" value="${o ? E(o.email || '') : ''}" placeholder="email@example.com" style="${inp}"></label>
            <label style="font-size:11px; color:var(--text-muted);">Phone<input type="text" class="owner-f-phone" value="${o ? E(o.phone || '') : ''}" placeholder="Phone" style="${inp}"></label>
            <label style="font-size:11px; color:var(--text-muted);">Notes<input type="text" class="owner-f-notes" value="${o ? E(o.notes || '') : ''}" placeholder="Notes" style="${inp}"></label>
          </div>
          <div style="margin-top:10px; font-size:11px; font-weight:700; color:var(--text-muted); text-transform:uppercase; letter-spacing:.05em;">Co-owners / spouse (same statement)</div>
          ${members.map((m, i) => `<div class="owner-f-member" style="display:grid; grid-template-columns:1fr 1fr 1.4fr 1fr 0.8fr; gap:6px; margin-top:5px;">
            <input type="text" class="owner-fm-first" value="${E(m.first_name)}" placeholder="First" style="${inp}"><input type="text" class="owner-fm-last" value="${E(m.last_name)}" placeholder="Last" style="${inp}"><input type="email" class="owner-fm-email" value="${E(m.email || '')}" placeholder="email (optional)" style="${inp}"><input type="text" class="owner-fm-phone" value="${E(m.phone || '')}" placeholder="phone (optional)" style="${inp}"><input type="text" class="owner-fm-role" value="${E(m.role || '')}" placeholder="${i === 0 ? 'spouse / partner' : 'role'}" style="${inp}">
          </div>`).join('')}
          <div style="margin-top:10px; font-size:11px; font-weight:700; color:var(--text-muted); text-transform:uppercase; letter-spacing:.05em;">Mailing address</div>
          <div style="display:grid; grid-template-columns:2fr 1.2fr 1.2fr 0.7fr 0.6fr; gap:6px; margin-top:5px;">
            <input type="text" class="owner-fa-line1" value="${E(a.line1 || '')}" placeholder="Street" style="${inp}"><input type="text" class="owner-fa-line2" value="${E(a.line2 || '')}" placeholder="Unit / suite" style="${inp}"><input type="text" class="owner-fa-city" value="${E(a.city || '')}" placeholder="City" style="${inp}"><input type="text" class="owner-fa-state" value="${E(a.state || '')}" placeholder="ST" style="${inp}"><input type="text" class="owner-fa-zip" value="${E(a.zip || '')}" placeholder="ZIP" style="${inp}">
          </div>
          <div style="display:flex; gap:10px; align-items:center; margin-top:10px; flex-wrap:wrap;">
            <label style="font-size:12px; display:flex; gap:6px; align-items:center;"><input type="checkbox" class="owner-f-active" ${checked}> Active</label>
            <span style="font-size:11.5px; color:var(--text-muted);">Contract · W-9 · Form 590 are uploaded on the owner's profile (Documents card).${o && o.contracts_url ? ` Legacy Drive link kept: <a href="${E(o.contracts_url)}" target="_blank" rel="noopener">open</a>` : ''}</span>
            <span style="margin-left:auto;">
              <button class="btn btn-primary btn-sm save-owner-btn" data-id="${idAttr}">Save</button>
              <button class="btn btn-secondary btn-sm cancel-owner-btn" style="margin-left:4px;">Cancel</button>
            </span>
          </div>
        </td>
      </tr>`;
  }

  // ── Properties section ───────────────────────────────────────

  private propertyTypeCounts(): Record<string, number> {
    const counts: Record<string, number> = {};
    PROPERTY_TYPE_OPTIONS.forEach(t => (counts[t.value] = 0));
    this.listings.forEach(l => {
      const t = this.propertyTypes[String(l.id)] || 'standard';
      counts[t] = (counts[t] || 0) + 1;
    });
    return counts;
  }

  private renderPropertiesSection(): string {
    if (!this.listings.length) {
      return `<div class="card">
        ${emptyState('No properties', 'No active listings were found. Properties sync from Hostaway.', '🏠')}
      </div>`;
    }

    const counts = this.propertyTypeCounts();
    const chips = PROPERTY_TYPE_OPTIONS.map(t =>
      `<span class="badge badge-accent" style="text-transform:none; font-size:11px; padding:3px 10px;">${t.label}: <strong>${counts[t.value] || 0}</strong></span>`
    ).join('');

    // Client-side search + pagination over the already-loaded active listings.
    const filtered = this.filteredProperties();
    const pg = paginate(filtered, this.propPage, this.propPageSize);
    this.propPage = pg.page; // adopt the clamped page

    const rows = pg.items.length
      ? pg.items.map(l => this.renderPropertyRow(l)).join('')
      : `<tr><td colspan="5">${emptyState('No properties found', 'No active listings match your search. Clear it to see the full list.', '🔍')}</td></tr>`;

    return `
      <div>
        <div class="card-head" style="border:1px solid var(--border); border-radius:var(--radius-lg) var(--radius-lg) 0 0; flex-direction:column; align-items:stretch; gap:var(--space-3);">
          <div style="display:flex; align-items:center; gap:var(--space-2); flex-wrap:wrap; min-width:0;">
            <span class="card-title" style="font-size:15px;">Properties</span>
            <span style="font-size:var(--fs-body); color:var(--text-muted);">${this.listings.length} active</span>
          </div>
          <div style="display:flex; gap:var(--space-2); flex-wrap:wrap;">${chips}</div>
          ${this.owners.length ? '' : callout('Add an owner above before you can assign properties.', 'tip')}
          ${filterBar({
            searchId: 'props-search',
            searchValue: this.propSearch,
            searchPlaceholder: 'Search property name or Hostaway id…',
            countLabel: `${pg.total} propert${pg.total === 1 ? 'y' : 'ies'}`,
          })}
        </div>
        <div class="card" style="border-radius:0 0 var(--radius-lg) var(--radius-lg); border-top:none;">
          <div class="u-table-wrap">
            <table class="data-table" style="min-width:880px;">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Hostaway id</th>
                  <th>Owner</th>
                  <th>Type</th>
                  <th>Comp tier ${infoIcon('comp_percentile')}</th>
                </tr>
              </thead>
              <tbody id="properties-table-body">
                ${rows}
              </tbody>
            </table>
          </div>
          <div style="padding:0 var(--space-4) var(--space-2);">
            ${pager({ idPrefix: 'props', page: pg.page, pages: pg.pages, total: pg.total, pageSize: this.propPageSize })}
          </div>
        </div>
      </div>`;
  }

  // Pure: active listings matching the property-table search (name/short name/id).
  private filteredProperties(): any[] {
    const s = this.propSearch.trim().toLowerCase();
    if (!s) return this.listings;
    return this.listings.filter(l => {
      const haystack = `${l.name || ''} ${l.short_name || ''} ${l.id ?? ''}`.toLowerCase();
      return haystack.includes(s);
    });
  }

  private renderPropertyRow(l: any): string {
    const id = String(l.id);
    const name = l.short_name || l.name || 'Unknown';
    const ownerId = this.assignments[id] || '';
    const type = this.propertyTypes[id] || 'standard';
    const pct = this.compPercentiles[id]; // may be undefined (uses default downstream)
    const sel = 'padding:5px 8px; font-size:var(--fs-body); border:1px solid var(--border); border-radius:var(--radius-sm); background:var(--card); color:var(--text); cursor:pointer; max-width:100%;';

    const ownerOpts = `<option value="">— Unassigned —</option>` +
      this.owners.map(o =>
        `<option value="${Formatters.escapeHtml(String(o.id))}" ${String(o.id) === String(ownerId) ? 'selected' : ''}>${Formatters.escapeHtml(o.name)}</option>`
      ).join('');

    const typeOpts = PROPERTY_TYPE_OPTIONS.map(t =>
      `<option value="${t.value}" ${t.value === type ? 'selected' : ''}>${t.label}</option>`
    ).join('');

    const compOpts = COMP_TIER_OPTIONS.map(c =>
      `<option value="${c.value}" ${pct != null && Number(pct) === c.value ? 'selected' : ''}>${c.label}</option>`
    ).join('');

    return `
      <tr>
        <td class="u-truncate" style="${TD} font-weight:500; max-width:280px;" title="${Formatters.escapeHtml(l.name || '')}">${Formatters.escapeHtml(name)}</td>
        <td style="${TD} color:var(--text-muted); font-family:ui-monospace,monospace; font-size:12px;">${Formatters.escapeHtml(id)}</td>
        <td style="${TD}">
          <select class="prop-owner-select" data-id="${Formatters.escapeHtml(id)}" style="${sel}" ${this.owners.length ? '' : 'disabled'}>${ownerOpts}</select>
        </td>
        <td style="${TD}">
          <select class="prop-type-select" data-id="${Formatters.escapeHtml(id)}" style="${sel}">${typeOpts}</select>
        </td>
        <td style="${TD}">
          <select class="prop-comp-select" data-id="${Formatters.escapeHtml(id)}" style="${sel}">${compOpts}</select>
        </td>
      </tr>`;
  }

  // ── Events ───────────────────────────────────────────────────

  private bindEvents(): void {
    // R117: publish a month of statements → owner accounts, assignments, portal statements
    document.getElementById('owners-publish-btn')?.addEventListener('click', () => {
      const host = document.getElementById('owners-publish-host');
      if (!host) return;
      const panel = new PublishStatements(host, () => { void this.loadData().then(() => this.renderBody()); });
      void panel.open();
      host.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    document.getElementById('add-owner-btn')?.addEventListener('click', () => {
      this.adding = true;
      this.editingOwnerId = null;
      this.renderBody();
    });

    const ownersBody = document.getElementById('owners-table-body');
    ownersBody?.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const editBtn = target.closest<HTMLButtonElement>('.edit-owner-btn');
      if (editBtn) { this.editingOwnerId = editBtn.dataset.id!; this.adding = false; this.renderBody(); return; }
      const cancelBtn = target.closest<HTMLButtonElement>('.cancel-owner-btn');
      if (cancelBtn) { this.adding = false; this.editingOwnerId = null; this.renderBody(); return; }
      const saveBtn = target.closest<HTMLButtonElement>('.save-owner-btn');
      if (saveBtn) { this.saveOwner(saveBtn.dataset.id || null, saveBtn); return; }
      const delBtn = target.closest<HTMLButtonElement>('.delete-owner-btn');
      if (delBtn) { this.deleteOwner(delBtn.dataset.id!, delBtn.dataset.name || ''); return; }
      const reportBtn = target.closest<HTMLButtonElement>('.owner-report-btn');
      if (reportBtn) {
        sessionStorage.setItem('reports_scope', reportBtn.dataset.id!);
        document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'reports' }));
        return;
      }
      // R83 #5: the owner's full profile page (name link + profile button).
      const profBtn = target.closest<HTMLElement>('.owner-profile-link');
      if (profBtn) {
        sessionStorage.setItem('owner_profile_id', (profBtn as HTMLElement).dataset.id!);
        document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'owner-profile' }));
        return;
      }
      const departBtn = target.closest<HTMLButtonElement>('.depart-owner-btn');
      if (departBtn) { this.markDeparted('owner', departBtn.dataset.id!, departBtn.dataset.name || 'Owner'); return; }
    });

    // R100 C5: CSV import — dry run → preview → apply
    const importFile = document.getElementById('owners-import-file') as HTMLInputElement | null;
    importFile?.addEventListener('change', async () => {
      const f = importFile.files && importFile.files[0];
      importFile.value = '';
      if (!f) return;
      const text = await f.text();
      this.importCsv = text; this.importPlan = { loading: true }; this.importBusy = false;
      const res = await this.postImport({ csv_text: text, dry_run: true });
      this.importPlan = res;
      this.renderBody();
    });
    document.getElementById('owners-import-cancel')?.addEventListener('click', () => { this.importPlan = null; this.importCsv = ''; this.renderBody(); });
    document.getElementById('owners-import-apply')?.addEventListener('click', async () => {
      const st = (this.importPlan && this.importPlan.stats) || {};
      if (!confirm(`Create ${st.create || 0} owner${st.create === 1 ? '' : 's'} and set ${st.assign || 0} property assignment${st.assign === 1 ? '' : 's'}?\n\nA backup of the current owners + assignments is written first. Conflicts and unmatched listings are left for you to assign by hand.`)) return;
      this.importBusy = true; this.renderBody();
      const res = await this.postImport({ csv_text: this.importCsv, dry_run: false });
      this.importBusy = false;
      this.importPlan = res;
      await this.loadData();
      this.renderBody();
    });

    document.getElementById('churn-add-property')?.addEventListener('click', () => {
      const name = window.prompt('Which property departed? (name as you want it logged)');
      if (!name) return;
      this.markDeparted('property', null, name.trim());
    });

    const propsBody = document.getElementById('properties-table-body');
    propsBody?.addEventListener('change', (e) => {
      const t = e.target as HTMLElement;
      const ownerSel = t.closest<HTMLSelectElement>('.prop-owner-select');
      if (ownerSel) { this.saveOwnerAssignment(ownerSel.dataset.id!, ownerSel.value); return; }
      const typeSel = t.closest<HTMLSelectElement>('.prop-type-select');
      if (typeSel) { this.savePropertyType(typeSel.dataset.id!, typeSel.value); return; }
      const compSel = t.closest<HTMLSelectElement>('.prop-comp-select');
      if (compSel) { this.saveCompPercentile(compSel.dataset.id!, parseInt(compSel.value, 10)); return; }
    });

    // Properties table: search + pagination (over already-loaded listings).
    const propSearch = document.getElementById('props-search') as HTMLInputElement | null;
    propSearch?.addEventListener('input', () => {
      this.propSearch = propSearch.value;
      this.propPage = 1;
      this.renderBody();
      const next = document.getElementById('props-search') as HTMLInputElement | null;
      if (next) { next.focus(); next.setSelectionRange(next.value.length, next.value.length); }
    });
    document.getElementById('props-prev')?.addEventListener('click', () => { this.propPage--; this.renderBody(); });
    document.getElementById('props-next')?.addEventListener('click', () => { this.propPage++; this.renderBody(); });
  }

  // ── Owner CRUD ───────────────────────────────────────────────

  private async saveOwner(id: string | null, btn: HTMLButtonElement): Promise<void> {
    const row = btn.closest('tr');
    if (!row) return;
    const v = (cls: string, root: Element = row) => (root.querySelector(`.${cls}`) as HTMLInputElement | null)?.value.trim() || '';
    const first = v('owner-f-first'), last = v('owner-f-last');
    const name = v('owner-f-name') || [first, last].filter(Boolean).join(' ');
    if (!name) { alert('First and last name (or a display name) are required.'); return; }
    const active = (row.querySelector('.owner-f-active') as HTMLInputElement | null)?.checked !== false;
    const members: OwnerMember[] = Array.from(row.querySelectorAll('.owner-f-member')).map(m => ({ first_name: v('owner-fm-first', m), last_name: v('owner-fm-last', m), email: v('owner-fm-email', m), phone: v('owner-fm-phone', m), role: v('owner-fm-role', m) })).filter(m => m.first_name || m.last_name || m.email);
    const mailing_address = { line1: v('owner-fa-line1'), line2: v('owner-fa-line2'), city: v('owner-fa-city'), state: v('owner-fa-state'), zip: v('owner-fa-zip') };

    const payload: Owner = {
      id: id || '',
      name, first_name: first, last_name: last, members, mailing_address,
      email: v('owner-f-email'),
      phone: v('owner-f-phone'),
      notes: v('owner-f-notes'),
      active,
    };
    if (!id) delete (payload as any).id; // POST without id => create

    btn.disabled = true; btn.textContent = 'Saving…';
    const res = await api.post<{ ok?: boolean; owner?: Owner; owners?: Owner[] }>('/owners', payload);
    if (!res || res.ok === false) {
      btn.disabled = false; btn.textContent = 'Save';
      alert('Could not save the owner. Please try again.');
      return;
    }
    this.adding = false;
    this.editingOwnerId = null;
    await this.loadData();
    this.renderBody();
  }

  private async deleteOwner(id: string, name: string): Promise<void> {
    if (!confirm(`Delete owner "${name}"? Their property assignments will be cleared.`)) return;
    const res = await api.post<{ ok?: boolean }>('/owners/delete', { id });
    if (!res || res.ok === false) { alert('Could not delete the owner. Please try again.'); return; }
    await this.loadData();
    this.renderBody();
  }

  // ── Property inline saves (no save button) ───────────────────

  private async saveOwnerAssignment(listingId: string, ownerId: string): Promise<void> {
    const prev = this.assignments[listingId];
    if (ownerId) this.assignments[listingId] = ownerId;
    else delete this.assignments[listingId];
    const res = await api.post<{ ok?: boolean }>('/listings/owner', { listing_id: listingId, owner_id: ownerId || null });
    if (!res || res.ok === false) {
      if (prev) this.assignments[listingId] = prev; else delete this.assignments[listingId];
      alert('Could not save the owner assignment. Please try again.');
    }
    // refresh owners section property counts
    this.renderBody();
  }

  private async savePropertyType(listingId: string, type: string): Promise<void> {
    const prev = this.propertyTypes[listingId];
    this.propertyTypes[listingId] = type;
    const res = await api.post<{ ok?: boolean }>('/listings/property-type', { id: listingId, type });
    if (!res || res.ok === false) {
      if (prev) this.propertyTypes[listingId] = prev; else delete this.propertyTypes[listingId];
      alert('Could not save the property type. Please try again.');
    }
    this.renderBody();
  }

  private async saveCompPercentile(listingId: string, percentile: number): Promise<void> {
    const prev = this.compPercentiles[listingId];
    this.compPercentiles[listingId] = percentile;
    const res = await api.post<{ ok?: boolean }>('/listings/comp-percentile', { id: listingId, percentile });
    if (!res || res.ok === false) {
      if (prev != null) this.compPercentiles[listingId] = prev; else delete this.compPercentiles[listingId];
      alert('Could not save the comp tier. Please try again.');
    }
  }
}
